import { useId, useMemo } from 'react'

import { Sparkles } from '@/assets/icons'
import { AgentV2Markdown } from '@/agent-v2/components/AgentV2Markdown'
import { unwrapPleadingCaptionsForDisplay } from '@/agent-v2/artifact-editor/custom_markdown_blocks/pleading-caption'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'
import { Button } from '@/components/button/Button'
import { MatterArtifactEditOperation } from '@/matter-artifacts/schemas'
import { buildMatterArtifactEditDiffMarkdown } from './build-matter-artifact-edit-diff'
import './AgentV2MatterArtifactEditReview.css'

type AgentV2MatterArtifactEditReviewProps = {
  selectedMarkdown: string
  replacementMarkdown: string
  referencesByCitationId: Record<string, AgentV2Reference>
  onCitationClick: (citationId: string) => void
  acceptingOperation: MatterArtifactEditOperation | null
  canAccept: boolean
  onAccept: (operation: MatterArtifactEditOperation) => void
  onReject: () => void
}

export default function AgentV2MatterArtifactEditReview({
  selectedMarkdown,
  replacementMarkdown,
  referencesByCitationId,
  onCitationClick,
  acceptingOperation,
  canAccept,
  onAccept,
  onReject,
}: AgentV2MatterArtifactEditReviewProps) {
  const titleId = useId()
  const diffMarkdown = useMemo(() => {
    const selectedPreview = unwrapPleadingCaptionsForDisplay(selectedMarkdown)
    const replacementPreview = unwrapPleadingCaptionsForDisplay(replacementMarkdown)
    return buildMatterArtifactEditDiffMarkdown(selectedPreview, replacementPreview)
  }, [replacementMarkdown, selectedMarkdown])
  const accepting = acceptingOperation !== null

  return (
    <div
      aria-busy={accepting}
      aria-labelledby={titleId}
      className="pointer-events-auto flex max-h-full min-h-0 flex-col gap-6 rounded-2xl bg-brand-neutral-50 p-6 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)]"
      data-agent-v2-artifact-edit-review
    >
      <div className="flex min-h-0 flex-col gap-4">
        <div className="flex shrink-0 items-center gap-2">
          <div className="flex items-center gap-2">
            <Sparkles aria-hidden className="h-6 w-6 shrink-0 text-brand-neutral-950" />
            <h2 className="text-lg font-bold text-brand-neutral-950" id={titleId}>
              Review suggested edit
            </h2>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto" data-agent-v2-artifact-edit-review-body>
          <div
            className="agent-v2-artifact-edit-review-content text-base leading-6 tracking-[-0.32px] text-brand-neutral-950 [&_del]:text-brand-neutral-500 [&_del]:decoration-brand-neutral-500 [&_ins]:bg-brand-100 [&_ins]:font-bold [&_ins]:text-brand-700 [&_ins]:no-underline"
            data-agent-v2-artifact-edit-review-content
          >
            <AgentV2Markdown onCitationClick={onCitationClick} referencesByCitationId={referencesByCitationId} text={diffMarkdown} />
          </div>
        </div>
      </div>

      {!canAccept && !accepting ? (
        <p className="text-sm text-brand-neutral-700" role="status">
          You can accept this edit when the response finishes.
        </p>
      ) : null}
      <div className="flex shrink-0 flex-col items-stretch gap-3 sm:flex-row sm:justify-end">
        <Button className="w-full min-w-[88px] sm:w-auto" color="neutral" disabled={accepting} onClick={onReject} size="md" variant="outlined">
          Reject
        </Button>
        <Button
          className="w-full min-w-[104px] sm:w-auto"
          color="primary"
          disabled={!canAccept || !replacementMarkdown.trim()}
          loading={acceptingOperation === MatterArtifactEditOperation.INSERT_AFTER}
          onClick={() => onAccept(MatterArtifactEditOperation.INSERT_AFTER)}
          size="md"
          variant="outlined"
        >
          Insert Below
        </Button>
        <Button
          className="w-full min-w-[88px] sm:w-auto"
          color="primary"
          disabled={!canAccept}
          loading={acceptingOperation === MatterArtifactEditOperation.REPLACE}
          onClick={() => onAccept(MatterArtifactEditOperation.REPLACE)}
          size="md"
          variant="filled"
        >
          Replace
        </Button>
      </div>
    </div>
  )
}
